import { parseMarkdownHeadings } from "@/lib/article-toc/parse-markdown-headings";

export const MARKDOWN_BLOCK_MARKER = "[[FOODANGO_MARKDOWN]]";

export type RawHeading = {
  label: string;
  anchorKey: string;
  markdownBlockKey: string | null;
  markdownLine: number | null;
};

export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

export const extractInlineText = (children: unknown): string => {
  if (!Array.isArray(children)) {
    return "";
  }

  const parts: string[] = [];

  for (const child of children) {
    if (!isRecord(child) || typeof child.type !== "string") {
      continue;
    }

    if (child.type === "text" && typeof child.text === "string") {
      parts.push(child.text);
      continue;
    }

    if (Array.isArray(child.children)) {
      parts.push(extractInlineText(child.children));
    }
  }

  return parts.join("");
};

export const getMarkdownContentFromBlock = (block: unknown): string | null => {
  if (!isRecord(block) || block.type !== "code") {
    return null;
  }

  const textContent = extractInlineText(block.children);

  if (!textContent.trimStart().startsWith(MARKDOWN_BLOCK_MARKER)) {
    return null;
  }

  const markerIndex = textContent.indexOf(MARKDOWN_BLOCK_MARKER);
  const markdown = textContent
    .slice(markerIndex + MARKDOWN_BLOCK_MARKER.length)
    .replace(/^\r?\n/, "");

  return markdown.trim().length > 0 ? markdown : null;
};

export const extractHeadingsFromBlocks = (
  content: unknown,
  keyPrefix: string,
): RawHeading[] => {
  if (!Array.isArray(content)) {
    return [];
  }

  const headings: RawHeading[] = [];

  content.forEach((block, index) => {
    const blockKey = `${keyPrefix}b:${index}`;

    if (isRecord(block) && block.type === "heading" && block.level === 2) {
      const label = extractInlineText(block.children).trim();

      if (label) {
        headings.push({
          label,
          anchorKey: blockKey,
          markdownBlockKey: null,
          markdownLine: null,
        });
      }

      return;
    }

    const markdown = getMarkdownContentFromBlock(block);

    if (!markdown) {
      return;
    }

    for (const { label, line } of parseMarkdownHeadings(markdown)) {
      headings.push({
        label,
        anchorKey: `${blockKey}:md:${line}`,
        markdownBlockKey: blockKey,
        markdownLine: line,
      });
    }
  });

  return headings;
};
